import React, { useState, useMemo } from 'react';
import { 
  Target, 
  Search, 
  Filter, 
  Plus, 
  Edit, 
  Eye, 
  TrendingUp, 
  Calendar, 
  Mail, 
  Phone, 
  MapPin,
  Star,
  Clock,
  User,
  Building,
  ArrowLeft,
  Activity,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { leads } from '../../data/salesCycleData';

const LeadTracking = ({ onBack, navigationSource }) => {
  const [activeView, setActiveView] = useState('dashboard');
  const [selectedLead, setSelectedLead] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [stageFilter, setStageFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [sortField, setSortField] = useState('score');
  const [sortDirection, setSortDirection] = useState('desc');

  // Helper functions
  const getStatusColor = (status) => {
    const colors = {
      'new': 'bg-blue-100 text-blue-800',
      'qualified': 'bg-green-100 text-green-800',
      'unqualified': 'bg-red-100 text-red-800',
      'contacted': 'bg-yellow-100 text-yellow-800',
      'converted': 'bg-purple-100 text-purple-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'high': 'bg-red-100 text-red-800',
      'medium': 'bg-yellow-100 text-yellow-800',
      'low': 'bg-green-100 text-green-800'
    };
    return colors[priority] || 'bg-gray-100 text-gray-800';
  };

  const getStageColor = (stage) => {
    const colors = {
      'initial_contact': 'bg-blue-100 text-blue-800',
      'qualification': 'bg-yellow-100 text-yellow-800',
      'proposal': 'bg-orange-100 text-orange-800',
      'negotiation': 'bg-purple-100 text-purple-800',
      'closed_won': 'bg-green-100 text-green-800',
      'closed_lost': 'bg-red-100 text-red-800'
    };
    return colors[stage] || 'bg-gray-100 text-gray-800';
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    if (score >= 40) return 'text-orange-600';
    return 'text-red-600';
  };

  const formatCurrency = (amount, currency = 'TWD') => {
    return new Intl.NumberFormat('zh-TW', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getStageLabel = (stage) => {
    const labels = {
      'initial_contact': '初次接觸',
      'qualification': '資格審查',
      'proposal': '提案階段',
      'negotiation': '談判階段',
      'closed_won': '成交',
      'closed_lost': '失敗'
    };
    return labels[stage] || stage;
  };

  const getStatusLabel = (status) => {
    const labels = {
      'new': '新潛客',
      'qualified': '已認證',
      'unqualified': '未認證',
      'contacted': '已聯絡',
      'converted': '已轉換'
    };
    return labels[status] || status;
  };

  const getPriorityLabel = (priority) => {
    const labels = {
      'high': '高',
      'medium': '中',
      'low': '低'
    };
    return labels[priority] || priority;
  };

  // Filtering and sorting
  const filteredLeads = useMemo(() => {
    return leads.filter(lead => {
      if (statusFilter !== 'all' && lead.status !== statusFilter) {
        return false;
      }
      if (stageFilter !== 'all' && lead.stage !== stageFilter) {
        return false;
      }
      if (priorityFilter !== 'all' && lead.priority !== priorityFilter) {
        return false;
      }
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        return (
          lead.company_name.toLowerCase().includes(searchLower) ||
          lead.contact_person.toLowerCase().includes(searchLower) ||
          lead.assigned_to.toLowerCase().includes(searchLower)
        );
      }
      return true;
    });
  }, [statusFilter, stageFilter, priorityFilter, searchTerm]);

  const sortedLeads = useMemo(() => {
    return [...filteredLeads].sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'score':
          comparison = a.score - b.score;
          break;
        case 'estimated_value':
          comparison = a.estimated_value - b.estimated_value;
          break;
        case 'probability':
          comparison = a.probability - b.probability;
          break;
        case 'company_name':
          comparison = a.company_name.localeCompare(b.company_name);
          break;
        case 'estimated_close_date':
          comparison = new Date(a.estimated_close_date) - new Date(b.estimated_close_date);
          break;
        default:
          comparison = 0;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [filteredLeads, sortField, sortDirection]);

  const renderWorkflowBanner = () => {
    if (navigationSource === 'sales-workflow') {
      return (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-500 p-4 mb-6 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-blue-500 p-2 rounded-full">
                <Activity className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-blue-800">來自業務流程: 銷售週期管理</h3>
                <p className="text-sm text-blue-600">階段A: 潛在客戶識別與開發 - 潛客追蹤</p>
              </div>
            </div>
            <button
              onClick={onBack}
              className="flex items-center space-x-2 bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>返回銷售週期</span>
            </button>
          </div>
        </div>
      );
    }
    return null;
  };

  const renderDashboard = () => {
    const totalLeads = leads.length;
    const qualifiedLeads = leads.filter(l => l.status === 'qualified').length;
    const totalValue = leads.reduce((sum, l) => sum + l.estimated_value, 0);
    const avgScore = leads.reduce((sum, l) => sum + l.score, 0) / leads.length;
    const highPriorityLeads = leads.filter(l => l.priority === 'high').length;

    return (
      <div className="space-y-6">
        {renderWorkflowBanner()}
        
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-800">潛客追蹤儀表板</h2>
          <button
            onClick={() => setActiveView('create')}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>新增潛客</span>
          </button>
        </div>

        {/* 統計卡片 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">潛客總數</p>
                <p className="text-2xl font-bold text-gray-800">{totalLeads}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-full">
                <Target className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">已認證潛客</p>
                <p className="text-2xl font-bold text-gray-800">{qualifiedLeads}</p>
              </div>
              <div className="bg-green-100 p-3 rounded-full">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">預估總價值</p>
                <p className="text-2xl font-bold text-gray-800">{formatCurrency(totalValue)}</p>
              </div>
              <div className="bg-purple-100 p-3 rounded-full">
                <TrendingUp className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">平均評分</p>
                <p className="text-2xl font-bold text-gray-800">{avgScore.toFixed(1)}</p>
              </div>
              <div className="bg-yellow-100 p-3 rounded-full">
                <Star className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">高優先級</p>
                <p className="text-2xl font-bold text-gray-800">{highPriorityLeads}</p>
              </div>
              <div className="bg-red-100 p-3 rounded-full">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </div>
        </div>

        {/* 潛客管道分析 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">潛客來源分析</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">展會活動</span>
                <div className="flex items-center space-x-2">
                  <div className="w-24 bg-gray-200 rounded-full h-2">
                    <div className="bg-blue-600 h-2 rounded-full" style={{ width: '40%' }}></div>
                  </div>
                  <span className="text-sm font-medium">40%</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">官網詢價</span>
                <div className="flex items-center space-x-2">
                  <div className="w-24 bg-gray-200 rounded-full h-2">
                    <div className="bg-green-600 h-2 rounded-full" style={{ width: '30%' }}></div>
                  </div>
                  <span className="text-sm font-medium">30%</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">客戶推薦</span>
                <div className="flex items-center space-x-2">
                  <div className="w-24 bg-gray-200 rounded-full h-2">
                    <div className="bg-purple-600 h-2 rounded-full" style={{ width: '20%' }}></div>
                  </div>
                  <span className="text-sm font-medium">20%</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">其他</span>
                <div className="flex items-center space-x-2">
                  <div className="w-24 bg-gray-200 rounded-full h-2">
                    <div className="bg-yellow-600 h-2 rounded-full" style={{ width: '10%' }}></div>
                  </div>
                  <span className="text-sm font-medium">10%</span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">階段分布</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">初次接觸</span>
                <div className="flex items-center space-x-2">
                  <div className="w-24 bg-gray-200 rounded-full h-2">
                    <div className="bg-blue-600 h-2 rounded-full" style={{ width: '35%' }}></div>
                  </div>
                  <span className="text-sm font-medium">35%</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">提案階段</span>
                <div className="flex items-center space-x-2">
                  <div className="w-24 bg-gray-200 rounded-full h-2">
                    <div className="bg-orange-600 h-2 rounded-full" style={{ width: '25%' }}></div>
                  </div>
                  <span className="text-sm font-medium">25%</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">談判階段</span>
                <div className="flex items-center space-x-2">
                  <div className="w-24 bg-gray-200 rounded-full h-2">
                    <div className="bg-purple-600 h-2 rounded-full" style={{ width: '30%' }}></div>
                  </div>
                  <span className="text-sm font-medium">30%</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">已成交</span>
                <div className="flex items-center space-x-2">
                  <div className="w-24 bg-gray-200 rounded-full h-2">
                    <div className="bg-green-600 h-2 rounded-full" style={{ width: '10%' }}></div>
                  </div>
                  <span className="text-sm font-medium">10%</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 高優先級潛客 */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800">高優先級潛客</h3>
              <button
                onClick={() => setActiveView('list')}
                className="text-blue-600 hover:text-blue-800 font-medium"
              >
                查看全部
              </button>
            </div>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {leads.filter(l => l.priority === 'high').map((lead) => (
                <div
                  key={lead.id}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                  onClick={() => {
                    setSelectedLead(lead);
                    setActiveView('detail');
                  }}
                >
                  <div className="flex items-center space-x-4">
                    <div className="bg-blue-100 p-3 rounded-full">
                      <Building className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-800">{lead.company_name}</h4>
                      <p className="text-sm text-gray-600">{lead.contact_person} - {lead.assigned_to}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <p className="font-semibold text-gray-800">{formatCurrency(lead.estimated_value)}</p>
                      <p className="text-sm text-gray-600">{lead.probability}% 機率</p>
                    </div>
                    <div className="text-right">
                      <p className={`text-2xl font-bold ${getScoreColor(lead.score)}`}>{lead.score}</p>
                      <p className="text-sm text-gray-600">評分</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderList = () => (
    <div className="space-y-6">
      {renderWorkflowBanner()}
      
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-800">潛客列表</h2>
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setActiveView('create')}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>新增潛客</span>
          </button>
          <button
            onClick={() => setActiveView('dashboard')}
            className="flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>返回儀表板</span>
          </button>
        </div>
      </div>

      {/* 搜尋和篩選 */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="搜尋潛客..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">所有狀態</option>
            <option value="new">新潛客</option>
            <option value="qualified">已認證</option>
            <option value="contacted">已聯絡</option>
            <option value="converted">已轉換</option>
          </select>
          
          <select
            value={stageFilter}
            onChange={(e) => setStageFilter(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">所有階段</option>
            <option value="initial_contact">初次接觸</option>
            <option value="qualification">資格審查</option>
            <option value="proposal">提案階段</option>
            <option value="negotiation">談判階段</option>
          </select>
          
          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">所有優先級</option>
            <option value="high">高</option>
            <option value="medium">中</option>
            <option value="low">低</option>
          </select>
          
          <select
            value={sortField}
            onChange={(e) => setSortField(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="score">評分</option>
            <option value="estimated_value">預估價值</option>
            <option value="probability">成交機率</option>
            <option value="company_name">公司名稱</option>
            <option value="estimated_close_date">預估成交日期</option>
          </select>
        </div>
      </div>

      {/* 潛客表格 */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                潛客資訊
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                評分
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                預估價值
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                階段
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                優先級
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                負責人
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                操作
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedLeads.map((lead) => (
              <tr key={lead.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="bg-blue-100 p-2 rounded-full mr-3">
                      <Building className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900">{lead.company_name}</div>
                      <div className="text-sm text-gray-500">{lead.contact_person}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <span className={`text-2xl font-bold ${getScoreColor(lead.score)}`}>{lead.score}</span>
                    <div className="ml-2 w-16 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full" 
                        style={{ width: `${lead.score}%` }}
                      ></div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{formatCurrency(lead.estimated_value)}</div>
                  <div className="text-sm text-gray-500">{lead.probability}% 機率</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStageColor(lead.stage)}`}>
                    {getStageLabel(lead.stage)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(lead.priority)}`}>
                    {getPriorityLabel(lead.priority)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {lead.assigned_to}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button
                    onClick={() => {
                      setSelectedLead(lead);
                      setActiveView('detail');
                    }}
                    className="text-blue-600 hover:text-blue-900 mr-3"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => {
                      setSelectedLead(lead);
                      setActiveView('edit');
                    }}
                    className="text-green-600 hover:text-green-900"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderDetail = () => {
    if (!selectedLead) return null;

    return (
      <div className="space-y-6">
        {renderWorkflowBanner()}
        
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-800">潛客詳細資訊</h2>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setActiveView('edit')}
              className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
            >
              <Edit className="w-4 h-4" />
              <span>編輯潛客</span>
            </button>
            <button
              onClick={() => setActiveView('list')}
              className="flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>返回列表</span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* 基本資訊 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">基本資訊</h3>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600">公司名稱</p>
                <p className="font-medium text-gray-800">{selectedLead.company_name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">聯絡人</p>
                <p className="font-medium text-gray-800">{selectedLead.contact_person}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">職稱</p>
                <p className="font-medium text-gray-800">{selectedLead.contact_title}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">行業</p>
                <p className="font-medium text-gray-800">{selectedLead.industry_name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">國家</p>
                <p className="font-medium text-gray-800">{selectedLead.country}</p>
              </div>
            </div>
          </div>

          {/* 銷售資訊 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">銷售資訊</h3>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600">潛客評分</p>
                <div className="flex items-center space-x-2">
                  <span className={`text-2xl font-bold ${getScoreColor(selectedLead.score)}`}>
                    {selectedLead.score}
                  </span>
                  <div className="w-24 bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full" 
                      style={{ width: `${selectedLead.score}%` }}
                    ></div>
                  </div>
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-600">預估價值</p>
                <p className="font-medium text-gray-800">{formatCurrency(selectedLead.estimated_value)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">成交機率</p>
                <p className="font-medium text-gray-800">{selectedLead.probability}%</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">預估成交日期</p>
                <p className="font-medium text-gray-800">{selectedLead.estimated_close_date}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">負責業務</p>
                <p className="font-medium text-gray-800">{selectedLead.assigned_to}</p>
              </div>
            </div>
          </div>

          {/* 狀態與階段 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">狀態與階段</h3>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600">潛客狀態</p>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedLead.status)}`}>
                  {getStatusLabel(selectedLead.status)}
                </span>
              </div>
              <div>
                <p className="text-sm text-gray-600">銷售階段</p>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStageColor(selectedLead.stage)}`}>
                  {getStageLabel(selectedLead.stage)}
                </span>
              </div>
              <div>
                <p className="text-sm text-gray-600">優先級</p>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(selectedLead.priority)}`}>
                  {getPriorityLabel(selectedLead.priority)}
                </span>
              </div>
              <div>
                <p className="text-sm text-gray-600">潛客來源</p>
                <p className="font-medium text-gray-800">{selectedLead.lead_source_detail}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">下次行動</p>
                <p className="font-medium text-gray-800">{selectedLead.next_action_date}</p>
              </div>
            </div>
          </div>
        </div>

        {/* 互動歷史 */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">互動歷史</h3>
          <div className="space-y-4">
            {selectedLead.interactions.map((interaction) => (
              <div key={interaction.id} className="border-l-4 border-blue-500 pl-4 py-2">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-gray-800">{interaction.subject}</h4>
                  <span className="text-sm text-gray-500">{interaction.date}</span>
                </div>
                <p className="text-sm text-gray-600 mt-1">{interaction.notes}</p>
                {interaction.next_steps && (
                  <p className="text-sm text-blue-600 mt-1">下一步: {interaction.next_steps}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (activeView) {
      case 'dashboard':
        return renderDashboard();
      case 'list':
        return renderList();
      case 'detail':
        return renderDetail();
      case 'create':
        return (
          <div className="space-y-6">
            {renderWorkflowBanner()}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">新增潛客</h2>
              <p className="text-gray-600">潛客新增功能開發中...</p>
              <button
                onClick={() => setActiveView('dashboard')}
                className="mt-4 flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>返回儀表板</span>
              </button>
            </div>
          </div>
        );
      case 'edit':
        return (
          <div className="space-y-6">
            {renderWorkflowBanner()}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">編輯潛客</h2>
              <p className="text-gray-600">潛客編輯功能開發中...</p>
              <button
                onClick={() => setActiveView('detail')}
                className="mt-4 flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>返回詳細資訊</span>
              </button>
            </div>
          </div>
        );
      default:
        return renderDashboard();
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-7xl mx-auto">
        {renderContent()}
      </div>
    </div>
  );
};

export default LeadTracking; 